import React, { useEffect, useMemo, useState } from "react";

import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";

import { DataTableNew } from "@/components/shared/DataTable";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import SearchInput from "@/components/shared/SearchInput";
import Skeleton from "@/components/shared/Skeleton";
import { Filter, FilterPill } from "@/components/shared/Filter";
import PermissionGuard from "@/permissions/PermissionGuard";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import { MODULES } from "@/permissions/modules";
import { PERMISSIONS } from "@/permissions/constant";
import { hasPermission, getUserPermissions } from "@/permissions/rbac";
import { useAuth } from "@/hooks/useAuth";
import { formatDate } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";

import { ComplianceService } from "@/services/complianceService";
import useComplianceLiveUpdates from "../hooks/useComplianceLiveUpdates";
import { COMPLIANCE_STATUS, complianceStatusFilterOption, getComplianceStatusMeta, SLA_OVERDUE_DAYS, SLA_WARNING_DAYS } from "../constant";

const DAY_MS = 86_400_000;

// Document Intake — the compliance-candidate list (candidates already "Sent to
// Compliance"). Rehomed from the former /admin/compliance landing under the new
// COMPLIANCE sidebar section (plan §13b, D8). Same data source
// (ComplianceService.getCandidates → only ComplianceCandidate rows); row-click
// still opens the detail/review screen.
const DocumentIntake = () => {
  const setHeader = useOutletContext();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Row click-through to the review screen requires COMPLIANCE_REVIEW (D11) —
  // without it the row would just bounce off the route guard into an
  // Unauthorized page, so gate the click itself instead.
  const canReview = hasPermission(PERMISSIONS.COMPLIANCE_REVIEW, getUserPermissions(user));

  // Helper to parse comma-separated status query param into an array
  const parseStatusParam = (param) => {
    if (!param) return [];
    return param
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  };

  // Status filter is URL-backed (?status=) so the dashboard widgets can
  // deep-link into a pre-filtered Intake. Seed initial state from URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStatus = searchParams.get("status") || "";

  const [search, setSearch] = useState("");
  const [statusFilters, setStatusFilters] = useState(() => parseStatusParam(urlStatus));
  // Both narrow by the candidate's assigned checklist: `checklist` matches
  // its name (partial), `region` its region (exact, from the dropdown).
  const [checklistFilter, setChecklistFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [regionOptions, setRegionOptions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [tableData, setTableData] = useState({ total: 0, isLoading: false });

  // Reflect filter change back into URL (?status=pending_review,stuck)
  const setStatusFiltersAndUrl = (values) => {
    const arr = Array.isArray(values) ? values : values ? [values] : [];
    setStatusFilters(arr);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (arr.length > 0) next.set("status", arr.join(","));
        else next.delete("status");
        return next;
      },
      { replace: true },
    );
  };

  // Keep local state aligned if the URL changes underneath us
  useEffect(() => {
    const parsed = parseStatusParam(urlStatus);
    setStatusFilters((prev) => {
      const isSame = prev.length === parsed.length && prev.every((v, i) => v === parsed[i]);
      return isSame ? prev : parsed;
    });
  }, [urlStatus]);

  // Regions in use, for the filter dropdown.
  useEffect(() => {
    let active = true;
    ComplianceService.getChecklistRegions()
      .then((regions) => {
        if (active) setRegionOptions(regions.map((r) => ({ value: r, label: r })));
      })
      .catch(() => {
        if (active) setRegionOptions([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setHeader({
      title: "Document Intake",
      subTitle: tableData.isLoading ? <Skeleton className="h-5 w-28" /> : `Total ${tableData.total} candidates`,
    });
    return () => setHeader({ title: "", subTitle: "" });
  }, [tableData, setHeader]);

  const onDataFetch = (data) => setTableData(data);

  const COLUMNS = [
    {
      key: "candidateName",
      label: "Candidate",
      sortable: true,
      align: "left",
      render: (val) => <span className="text-left font-medium text-[13px]">{val ?? "—"}</span>,
    },
    {
      key: "candidateEmail",
      label: "Email",
      sortable: true,
      align: "left",
      render: (val) => <span className="text-left text-text-primary text-xs">{val ?? "—"}</span>,
    },
    {
      key: "assignedChecklistName",
      label: "Checklist",
      sortable: true,
      align: "left",
      render: (val) => <span className="text-text-primary text-xs">{val ?? "—"}</span>,
    },
    {
      key: "documentCount",
      label: "Documents",
      sortable: true,
      align: "center",
      render: (val) => <span className="text-text-primary text-xs">{val ?? 0}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      align: "center",
      render: (val) => {
        const meta = getComplianceStatusMeta(val);
        return (
          <Badge
            label={meta.label}
            badgeStyle="text-[11px]"
            variant="gradient"
            colorIndex={meta.colorIndex}
            className="min-h-6 border-[1px] text-center rounded-[2rem]"
          />
        );
      },
    },
    {
      key: "createdAt",
      label: "Sent Date",
      sortable: true,
      align: "center",
      wrap: true,
      // The date alone does not say whether a candidate has been waiting
      // too long, so an SLA badge sits under it. Approved candidates are
      // settled and never flagged.
      render: (val, row) => {
        const sentTime = val ? new Date(val).getTime() : 0;
        const diffDays = sentTime > 0 ? Math.floor((Date.now() - sentTime) / DAY_MS) : 0;
        const isApproved = row?.status === COMPLIANCE_STATUS.APPROVED;
        const isOverdue = diffDays >= SLA_OVERDUE_DAYS && !isApproved;
        const isWarning = diffDays >= SLA_WARNING_DAYS && diffDays < SLA_OVERDUE_DAYS && !isApproved;

        return (
          <div className="flex flex-col items-center justify-center gap-0.5">
            <span className="text-text-primary text-xs">{val ? formatDate(val) : "—"}</span>
            {val && diffDays > 0 && !isApproved && (
              <span
                className={cn(
                  "text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full border leading-tight whitespace-nowrap",
                  isOverdue
                    ? "bg-tone-rose-soft text-tone-rose border-tone-rose/30"
                    : isWarning
                      ? "bg-tone-amber-soft text-tone-amber border-tone-amber/30"
                      : "bg-background-form text-text-primary/50 border-border-primary/60",
                )}
              >
                {isOverdue ? "Overdue ⚠️" : `${diffDays}d in queue`}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  const filters = useMemo(
    () => [
      {
        type: "multiselect",
        key: "status",
        label: "All Statuses",
        value: statusFilters,
        onChange: setStatusFiltersAndUrl,
        options: complianceStatusFilterOption,
      },
      {
        type: "input",
        key: "checklist",
        label: "Filter by checklist name...",
        value: checklistFilter,
        onChange: setChecklistFilter,
      },
      {
        // Regions that actually have a checklist — a candidate's region
        // is their assigned checklist's region.
        type: "select",
        key: "region",
        label: "All regions",
        value: regionFilter,
        onChange: setRegionFilter,
        options: regionOptions,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusFilters, checklistFilter, regionFilter, regionOptions],
  );

  const activeFilters = useMemo(() => {
    const statusPills = statusFilters.map((sVal) => {
      const meta = complianceStatusFilterOption.find((o) => o.value === sVal);
      return {
        key: `status-${sVal}`,
        label: `Status: ${meta?.label ?? sVal}`,
        clear: () => setStatusFiltersAndUrl(statusFilters.filter((s) => s !== sVal)),
      };
    });

    return [
      ...statusPills,
      checklistFilter && {
        key: "checklist",
        label: `Checklist: ${checklistFilter}`,
        clear: () => setChecklistFilter(""),
      },
      regionFilter && {
        key: "region",
        label: `Region: ${regionFilter}`,
        clear: () => setRegionFilter(""),
      },
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilters, checklistFilter, regionFilter]);

  const clearAllFilters = () => {
    setStatusFiltersAndUrl([]);
    setChecklistFilter("");
    setRegionFilter("");
  };

  // DataTableNew owns its own fetch lifecycle, so a live update refetches by
  // bumping this rather than by faking a query-param change (which would send
  // a junk param to the server). `getCandidates` keys its dedupe cache by
  // query, so the hook clears the whole cache — the established blunt route
  // for parameterised keys (see complianceService.js).
  const [reloadKey, setReloadKey] = useState(0);
  useComplianceLiveUpdates({
    clearAll: true,
    onRefresh: () => setReloadKey((k) => k + 1),
  });

  const extraParams = useMemo(
    () => ({
      status: statusFilters.length > 0 ? statusFilters.join(",") : undefined,
      checklist: checklistFilter || undefined,
      region: regionFilter || undefined,
    }),
    [statusFilters, checklistFilter, regionFilter],
  );

  return (
    <PermissionGuard permission={PERMISSIONS.COMPLIANCE_VIEW}>
      <div className="flex flex-col gap-2.5" style={{ zoom: 0.9 }}>
        {/* This is the review queue, so the state matters here: the list
                    reads normally and every candidate opens, but no decision on
                    one can be recorded until Compliance is back. */}
        {/* Keep `<ModuleUnavailableNotice module={MODULES.COMPLIANCE}` on one
                    line: scripts/check-module-surfaces.mjs matches that exact string
                    to prove this surface is gated, and it reads line by line. */}
        <ModuleUnavailableNotice module={MODULES.COMPLIANCE} suffix="Candidates still open for reading. Review decisions are paused." />

        {/* Search + Filters */}
        <div className="flex items-center gap-2.5 pt-1">
          <div className="flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Search by candidate name or email" />
          </div>

          <Filter
            filters={filters}
            activeFilters={activeFilters}
            showFilters={showFilters}
            onToggle={() => setShowFilters((p) => !p)}
            onClearAll={clearAllFilters}
            renderActiveFilter={(f) => <FilterPill key={f.key} label={f.label} onRemove={f.clear} />}
          />
        </div>

        <Card className="p-0">
          <DataTableNew
            key="compliance-candidates"
            columns={COLUMNS}
            api={ComplianceService.getCandidates}
            search={search}
            dataPath="candidates"
            onDataFetch={onDataFetch}
            extraParams={extraParams}
            reloadKey={reloadKey}
            onRowClick={canReview ? (row) => navigate(`/admin/compliance/${row.id}`) : undefined}
            paginationLabel="candidates"
            stickyHeader
            maxHeight="640px"
          />
        </Card>
      </div>
    </PermissionGuard>
  );
};

export default DocumentIntake;
